import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  signal,
  inject,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import {
  DataTableComponent,
  TableColumn,
} from '@app/shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { PurchaseReturnOrderService } from '../../../purchase-return-order.service';
import { GoodsStockResVO } from '@app/api/stock-service';

export interface PORStockSelectionItem {
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName: string;
  stockNum: number;
  /** HELD ("Đã dự trữ"): reservedNum + preemptionNum + lockNum, sourced from IMS. */
  heldNum: number;
  availableQuantity: number; // sourced from IMS
  warehouseCode?: string;
  warehouseName?: string;
}

@Component({
  selector: 'app-rtv-stock-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    TranslateModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './por-stock-selection-dialog.component.html',
  styleUrl: './por-stock-selection-dialog.component.css',
})
export class PORStockSelectionDialogComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() warehouseCode: string = '';
  @Input() existingSkuCodes: string[] = []; // SKU codes already added
  @Input() skuCodes?: string[]; // Optional: Filter by SKU codes (e.g., vendor SKUs)
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<PORStockSelectionItem[]>();

  private readonly porService = inject(PurchaseReturnOrderService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  loading = signal(false);
  stockItems = signal<PORStockSelectionItem[]>([]);
  selectedItems = signal<PORStockSelectionItem[]>([]);
  totalRecords = signal(0);

  // Table columns
  tableColumns: TableColumn[] = [
    {
      label: 'purchase_return_order.stock_dialog.columns.spu_code',
      prop: 'spuCode',
      width: 150,
      type: 'text',
      align: 'left',
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.spu_name',
      prop: 'spuName',
      width: 200,
      type: 'text',
      align: 'left',
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.sku_code',
      prop: 'skuCode',
      width: 150,
      type: 'text',
      align: 'left',
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.sku_name',
      prop: 'skuName',
      width: 200,
      type: 'text',
      align: 'left',
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.stock_num',
      prop: 'stockNum',
      width: 120,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.reserved_num',
      prop: 'heldNum',
      width: 120,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'purchase_return_order.stock_dialog.columns.available_quantity',
      prop: 'availableQuantity',
      width: 150,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
  ];

  ngOnInit(): void {
    // Load stock when component initializes if warehouseCode is available
    if (this.warehouseCode) {
      this.loadStock();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Load stock when dialog opens
    // Check both visible and warehouseCode changes to ensure API is called
    const visibleChanged = changes['visible'];
    const warehouseCodeChanged = changes['warehouseCode'];
    const skuCodesChanged = changes['skuCodes'];
    
    if (visibleChanged && visibleChanged.currentValue === true) {
      // Dialog is opening - load stock if warehouseCode is available
      if (this.warehouseCode) {
        this.loadStock();
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.warning'),
          detail: this.translateService.instant(
            'purchase_return_order.stock_dialog.errors.no_warehouse_selected'
          ),
        });
      }
    }
    
    // Reload stock when warehouseCode changes (and dialog is visible)
    if (warehouseCodeChanged && !warehouseCodeChanged.firstChange) {
      if (this.warehouseCode && this.visible) {
        this.loadStock();
      }
    }
    
    // Reload stock when skuCodes changes (and dialog is visible)
    // This handles the case when vendor is changed and vendor SKU codes are updated
    if (skuCodesChanged && !skuCodesChanged.firstChange) {
      if (this.warehouseCode && this.visible) {
        this.loadStock();
      }
    }

    // Reset selection when dialog closes
    if (visibleChanged && visibleChanged.currentValue === false) {
      this.selectedItems.set([]);
    }
  }

  /**
   * Load stock items from service
   */
  loadStock(): void {
    if (!this.warehouseCode) {
      return;
    }

    // If skuCodes is provided but empty, don't load stock (vendor has no SKUs)
    if (this.skuCodes !== undefined && this.skuCodes.length === 0) {
      this.loading.set(false);
      this.stockItems.set([]);
      this.totalRecords.set(0);
      this.selectedItems.set([]);
      return;
    }

    this.loading.set(true);
    this.selectedItems.set([]);

    this.porService.loadStock(this.warehouseCode, 0, 100, this.skuCodes).pipe(
      map((response: { items: (GoodsStockResVO & { availableQuantity: number; heldNum: number })[]; total: number }) => {
        // Map service response to PORStockSelectionItem format
        // Filter out items with availableQuantity = 0
        return response.items
          .map((item) => {
            // availableQuantity/heldNum are computed by IMS in
            // PurchaseReturnOrderService.loadStock() - not re-derived here from raw
            // stockNum/reservedNum.
            return {
              spuCode: item.spuCode || '',
              spuName: item.spuName || '',
              skuCode: item.skuCode || '',
              skuName: item.skuName || '',
              stockNum: item.stockNum || 0,
              heldNum: item.heldNum ?? 0,
              availableQuantity: item.availableQuantity ?? 0,
              warehouseCode: item.warehouseCode,
              warehouseName: item.warehouseName,
            } as PORStockSelectionItem;
          })
          .filter((item: PORStockSelectionItem) => {
            // Filter out items with availableQuantity = 0
            return item.availableQuantity > 0;
          });
      }),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant(
            'purchase_return_order.stock_dialog.errors.load_stock_failed'
          ),
        });
        return of([]);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (items: PORStockSelectionItem[]) => {
        this.stockItems.set(items);
        this.totalRecords.set(items.length);
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle lazy load for pagination
   */
  onLazyLoad(event: any): void {
    // For now, we load all items at once (size: 100)
    // Can be extended for pagination if needed
  }

  /**
   * Handle selection change
   */
  onSelectionChange(selected: PORStockSelectionItem[]): void {
    // Filter out items that are already added
    const filtered = selected.filter(
      item => !this.existingSkuCodes.includes(item.skuCode)
    );
    this.selectedItems.set(filtered);
  }

  /**
   * Check if item is already added
   */
  isItemAlreadyAdded(skuCode: string): boolean {
    return this.existingSkuCodes.includes(skuCode);
  }

  /**
   * Get selection state for row
   * Returns true if item is selected OR already added to table (to show checkmark for disabled items)
   */
  getSelectionState(item: PORStockSelectionItem): boolean {
    // Check if item is in selected items
    const isSelected = this.selectedItems().some(
      (selected) => selected.skuCode === item.skuCode
    );
    
    // Also check if item is already added (to show checkmark for disabled items)
    const isAlreadyAdded = this.isItemAlreadyAdded(item.skuCode);
    
    return isSelected || isAlreadyAdded;
  }

  /**
   * Check if row is disabled (already added to table)
   */
  isRowDisabled(item: PORStockSelectionItem): boolean {
    return this.isItemAlreadyAdded(item.skuCode);
  }

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    const selected = this.selectedItems();
    if (selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'purchase_return_order.stock_dialog.errors.no_item_selected'
        ),
      });
      return;
    }

    this.confirm.emit(selected);
    this.visibleChange.emit(false);
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.visibleChange.emit(false);
    this.selectedItems.set([]);
  }

  /**
   * Get dialog header
   */
  getDialogHeader(): string {
    return this.translateService.instant(
      'purchase_return_order.stock_dialog.title'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

